/**
 * Daily Photo List page — the list itself. Uploading vendor files and
 * generating a new list live on the dedicated New Photo List page
 * (/photos/daily/new-run), reached from the "Create New List" header button —
 * the same list/update split as the Inventory Audit and its New Audit Run
 * page.
 *
 * Wraps the page in PhotoListProvider (scoped context for this feature).
 * Loads available runs on mount, defaults to the latest.
 * Delegates rendering to DailyPhotoListPageContent which consumes the context.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { Plus, Printer, Settings } from "lucide-react"

import Layout from "../../components/Layout"
import SearchInput from "../../components/audit/SearchInput"
import AddCarToListModal from "../../components/photos/AddCarToListModal"
import RequestReshootModal from "../../components/photos/RequestReshootModal"
import ExportDropdown from "../../components/photos/ExportDropdown"
import PhotoListTable from "../../components/photos/PhotoListTable"
import PrintableSheets from "../../components/photos/PrintableSheets"
import SheetSelectionDropdown from "../../components/photos/SheetSelectionDropdown"
import DealerGroupRequiredNotice from "../../components/DealerGroupRequiredNotice"
import { useAuth } from "../../context/AuthContext"
import { useDealerGroup } from "../../context/DealerGroupContext"
import type { PhotoListVehicle } from "../../api/photoList"
import {
  PhotoListProvider,
  usePhotoList,
  PHOTO_LIST_SHEETS,
  RESHOOTS_SHEET_SLUG,
  type PhotoListStoreSheetSlug,
  PhotoListSheetSlug,
} from "../../context/PhotoListContext"
import { MOBILE_FULL_BUTTON } from "../../config/mobileStyles"
import { THEME } from "../../config/theme"
import { useIsMobile } from "../../hooks/useIsMobile"

export default function DailyPhotoListPage() {
  return (
    <Layout>
      <PhotoListProvider>
        <DailyPhotoListPageContent />
      </PhotoListProvider>
    </Layout>
  )
}

/** Case-insensitive substring match across the fields a photographer
 * actually hunts by: VIN, stock, year/make/model/trim, color, lot. Exported
 * for its unit test. */
export function vehicleMatchesSearch(vehicle: PhotoListVehicle, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [
    vehicle.vin,
    vehicle.stock,
    vehicle.year != null ? String(vehicle.year) : null,
    vehicle.make,
    vehicle.model,
    vehicle.trim,
    vehicle.color_ext,
    vehicle.stargard,
  ]
    .filter(Boolean)
    .some((field) => (field as string).toLowerCase().includes(q))
}

// ---------------------------------------------------------------------------
// Run selector (inline — mirrors RunSelector pattern)
// ---------------------------------------------------------------------------

function PhotoListRunSelector({ fullWidth = false }: { fullWidth?: boolean }) {
  const { currentRun, recentRuns, runsLoading, runsError, setCurrentRunId } = usePhotoList()

  if (runsLoading) {
    return (
      <div
        style={{
          color: THEME.colors.neutral.light.textSecondary,
          fontSize: 13,
        }}
      >
        Loading runs...
      </div>
    )
  }
  if (runsError) {
    return (
      <div style={{ color: THEME.colors.semantic.danger, fontSize: 13 }}>
        Failed to load runs
      </div>
    )
  }
  if (recentRuns.length === 0) {
    return (
      <div
        style={{
          color: THEME.colors.neutral.light.textSecondary,
          fontSize: 13,
        }}
      >
        No runs available.
      </div>
    )
  }

  function formatRunLabel(run: { started_at: string | null; status: string }, isLatest: boolean): string {
    if (!run.started_at) return "Unknown"
    const date = new Date(run.started_at)
    const label = date.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
    })
    const status = run.status === "success" ? "" : ` (${run.status})`
    return `${isLatest ? "Latest \u2014 " : ""}${label}${status}`
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: THEME.spacing.sm,
        ...(fullWidth && { width: "100%" }),
      }}
    >
      <label
        htmlFor="photo-list-run-selector"
        style={{
          fontSize: 13,
          color: THEME.colors.neutral.light.textPrimary,
          fontWeight: 500,
        }}
      >
        Run:
      </label>
      <select
        id="photo-list-run-selector"
        value={currentRun?.id ?? ""}
        onChange={(e) => setCurrentRunId(Number(e.target.value))}
        style={{
          padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
          fontSize: 13,
          borderRadius: THEME.borderRadius.md,
          border: `1px solid ${THEME.colors.silver}`,
          backgroundColor: THEME.colors.neutral.light.surface,
          color: THEME.colors.neutral.light.textPrimary,
          fontFamily: THEME.fonts.primary,
          cursor: "pointer",
          // Full-width mode (mobile) lets the select flex to the row instead
          // of forcing a 220px floor that would overflow a phone.
          ...(fullWidth ? { flex: 1, minWidth: 0 } : { minWidth: 220 }),
        }}
      >
        {recentRuns.map((run, idx) => (
          <option key={run.id} value={run.id}>
            {formatRunLabel(run, idx === 0)}
          </option>
        ))}
      </select>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Tab bar
// ---------------------------------------------------------------------------

function PhotoListTabs() {
  const { currentRun, activeSheet, setActiveSheet } = usePhotoList()
  const isMobile = useIsMobile()

  const countFor = (slug: string): number | null =>
    currentRun?.sheet_counts?.[slug as keyof typeof currentRun.sheet_counts] ?? null

  // On a phone the sheet strip forces a long horizontal scroll to find a
  // store — a native select gets the OS picker instead. Styled to match the
  // Run selector row above it.
  if (isMobile) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: THEME.spacing.sm,
          marginBottom: THEME.spacing.md,
        }}
      >
        <label
          htmlFor="photo-list-sheet-selector"
          style={{
            fontSize: 13,
            color: THEME.colors.neutral.light.textPrimary,
            fontWeight: 500,
          }}
        >
          Sheet:
        </label>
        <select
          id="photo-list-sheet-selector"
          value={activeSheet}
          onChange={(e) => setActiveSheet(e.target.value as PhotoListSheetSlug)}
          style={{
            padding: `${THEME.spacing.xs} ${THEME.spacing.sm}`,
            fontSize: 13,
            borderRadius: THEME.borderRadius.md,
            border: `1px solid ${THEME.colors.silver}`,
            backgroundColor: THEME.colors.neutral.light.surface,
            color: THEME.colors.neutral.light.textPrimary,
            fontFamily: THEME.fonts.primary,
            cursor: "pointer",
            flex: 1,
            minWidth: 0,
          }}
        >
          {PHOTO_LIST_SHEETS.map(({ slug, label }) => {
            const count = countFor(slug)
            return (
              <option key={slug} value={slug}>
                {label}
                {count !== null ? ` (${count.toLocaleString()})` : ""}
              </option>
            )
          })}
        </select>
      </div>
    )
  }

  const tabStyle = (slug: string): React.CSSProperties => {
    const isActive = slug === activeSheet
    return {
      padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
      cursor: "pointer",
      backgroundColor: "transparent",
      borderBottom: isActive
        ? `3px solid ${THEME.colors.navy}`
        : "3px solid transparent",
      color: isActive
        ? THEME.colors.navy
        : THEME.colors.neutral.light.textSecondary,
      fontWeight: isActive ? 600 : 400,
      fontSize: 13,
      userSelect: "none" as const,
      transition: "color 0.15s, border-color 0.15s",
      whiteSpace: "nowrap" as const,
    }
  }

  return (
    <div
      style={{
        display: "flex",
        gap: 0,
        borderBottom: `1px solid ${THEME.auditTable.borderColor}`,
        marginBottom: THEME.spacing.md,
        overflowX: "auto",
      }}
    >
      {PHOTO_LIST_SHEETS.map(({ slug, label }) => {
        const count = countFor(slug)
        return (
          <div
            key={slug}
            style={tabStyle(slug)}
            onClick={() => setActiveSheet(slug as PhotoListSheetSlug)}
          >
            {label}
            {count !== null && (
              <span
                style={{
                  marginLeft: 6,
                  color: THEME.colors.neutral.light.textSecondary,
                  fontWeight: 400,
                }}
              >
                ({count.toLocaleString()})
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page content
// ---------------------------------------------------------------------------

function DailyPhotoListPageContent() {
  const { dealerGroupId } = useDealerGroup()
  const isMobile = useIsMobile()
  const navigate = useNavigate()
  const location = useLocation()

  // The New Photo List page hands the finished run's count through navigation
  // state — its own success message can't render because generating navigates
  // here immediately. Shown for 5s; the state is cleared so a refresh doesn't
  // replay the banner.
  const [generatedTotal, setGeneratedTotal] = useState<number | null>(null)
  useEffect(() => {
    const state = location.state as { generatedTotal?: number } | null
    if (state?.generatedTotal != null) {
      setGeneratedTotal(state.generatedTotal)
      window.history.replaceState({}, "")
      const timer = setTimeout(() => setGeneratedTotal(null), 5000)
      return () => clearTimeout(timer)
    }
  }, [location.state])

  const {
    currentRun,
    activeSheet,
    vehicles,
    vehiclesLoading,
    vehiclesError,
    fetchSheetsForPrint,
    isLatestList,
    addVehicleByHand,
    removeVehicleAddedByHand,
    requestReshoot,
    removeReshoot,
  } = usePhotoList()
  const [addingCar, setAddingCar] = useState(false)
  // On the Reshoots sheet the Add button adds a reshoot (Ian, 2026-11-03);
  // on a store sheet it adds a car by hand to that sheet.
  const onReshootsSheet = activeSheet === RESHOOTS_SHEET_SLUG
  const { hasPermission } = useAuth()

  const activeLabel =
    PHOTO_LIST_SHEETS.find((s) => s.slug === activeSheet)?.label ?? activeSheet

  // Search filters the loaded sheet client-side (a few hundred rows at most
  // — no server round trip). Transient by design: not mirrored to the URL.
  const [searchText, setSearchText] = useState("")
  const visibleVehicles = useMemo(
    () => vehicles.filter((vehicle) => vehicleMatchesSearch(vehicle, searchText)),
    [vehicles, searchText],
  )

  // Preload the brand lockup so it's already cached when the user hits Print.
  // The PrintableSheets <img> only mounts after window.print() is queued, so
  // without this preload the print preview snapshots before the image loads.
  useEffect(() => {
    const img = new Image()
    img.src = "/lockup-on-light.png"
  }, [])

  // Print state
  const [printing, setPrinting] = useState(false)
  const [printVehicles, setPrintVehicles] = useState<Record<string, PhotoListVehicle[]>>({})
  const [printSheets, setPrintSheets] = useState<{ selected: string[]; order: string[] }>({
    selected: [],
    order: [],
  })

  const handlePrint = useCallback(
    async (selectedSheets: string[], sheetOrder: string[]) => {
      if (!currentRun) return

      // Bulk-fetch all selected sheets (uses cache when available)
      const vehiclesBySheet = await fetchSheetsForPrint(selectedSheets)

      // Make sure the lockup PNG is fully loaded before we trigger
      // the print dialog — otherwise the preview can snapshot before
      // the image decodes and the lockup slot prints empty. The
      // mount-time preload above usually has it cached already, in
      // which case this resolves immediately.
      await new Promise<void>((resolve) => {
        const img = new Image()
        img.onload = () => resolve()
        img.onerror = () => resolve()
        img.src = "/lockup-on-light.png"
        if (img.complete) resolve()
      })

      // Set print state — this renders PrintableSheets into the DOM
      setPrintVehicles(vehiclesBySheet)
      setPrintSheets({ selected: selectedSheets, order: sheetOrder })
      setPrinting(true)

      // Wait for React to render, then trigger browser print dialog
      requestAnimationFrame(() => {
        window.print()
        // After dialog closes (print or cancel), hide print container
        setPrinting(false)
      })
    },
    [currentRun, fetchSheetsForPrint],
  )

  // Uploads and run-triggering live on the New Photo List Run page — this
  // page only navigates there.
  const startNewRunButton = (
    <button
      type="button"
      onClick={() => navigate("/photos/daily/new-run")}
      style={{
        padding: `${THEME.spacing.sm} ${THEME.spacing.md}`,
        background: THEME.colors.navy,
        color: THEME.colors.white,
        fontWeight: 500,
        fontSize: 14,
        border: "none",
        borderRadius: THEME.borderRadius.md,
        cursor: "pointer",
        fontFamily: THEME.fonts.primary,
        whiteSpace: "nowrap",
        ...(isMobile && { ...MOBILE_FULL_BUTTON, flex: "initial", width: "100%" }),
      }}
    >
      Create New List
    </button>
  )

  // Icon-only entry to the Photo List Settings page (/photos/daily/settings) —
  // this feature's own secondary-page pattern, like startNewRunButton above.
  // Hidden without the manage permission (the route double-gates regardless).
  // Style matches components/photos/SheetSelectionDropdown.tsx ~line 175
  // (buttonStyle): navy fill, sm/md padding, md radius, 14px weight 500.
  // Icon-only rather than a 5th labeled control: on mobile it rides in the
  // existing Print/Export row and adds zero stack height.
  const settingsButton = hasPermission("manage_photo_list_settings") ? (
    <button
      type="button"
      aria-label="Photo list settings"
      title="Photo list settings"
      onClick={() => navigate("/photos/daily/settings")}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        padding: THEME.spacing.sm,
        background: THEME.colors.navy,
        color: THEME.colors.white,
        border: "none",
        borderRadius: THEME.borderRadius.md,
        cursor: "pointer",
        fontFamily: THEME.fonts.primary,
        ...(isMobile && { minWidth: 44, alignSelf: "stretch" }),
      }}
    >
      <Settings size={16} strokeWidth={2} />
    </button>
  ) : null

  // Working surface: under "All Groups" require a concrete group first.
  if (dealerGroupId === null) {
    return (
      <div style={{ padding: isMobile ? THEME.spacing.md : THEME.spacing.lg }}>
        <h1
          style={{
            fontSize: 24,
            fontWeight: 500,
            letterSpacing: "-0.5px",
            margin: `0 0 ${THEME.spacing.md} 0`,
            color: THEME.colors.neutral.light.textPrimary,
          }}
        >
          Daily Photo List
        </h1>
        <DealerGroupRequiredNotice surface="the daily photo list" />
      </div>
    )
  }

  return (
    <div style={{ padding: isMobile ? THEME.spacing.md : THEME.spacing.lg }}>
      {/* Header — stacks the title above full-width controls on mobile so the
          controls cluster wraps within the viewport instead of forcing
          horizontal scroll (mirrors pages/inventory/wholesale/AdesaRunListPage). */}
      <div
        style={{
          display: "flex",
          flexDirection: isMobile ? "column" : "row",
          justifyContent: "space-between",
          alignItems: isMobile ? "stretch" : "center",
          gap: isMobile ? THEME.spacing.sm : 0,
          marginBottom: THEME.spacing.md,
        }}
      >
        <div>
          <h1
            style={{
              fontSize: isMobile ? 20 : 24,
              fontWeight: 500,
              letterSpacing: "-0.5px",
              margin: 0,
              color: THEME.colors.neutral.light.textPrimary,
            }}
          >
            Daily Photo List
          </h1>
          {currentRun && (
            <div
              style={{
                fontSize: 13,
                color: THEME.colors.neutral.light.textSecondary,
                marginTop: 4,
              }}
            >
              {currentRun.total_count.toLocaleString()} vehicles across {PHOTO_LIST_SHEETS.length} sheets
            </div>
          )}
        </div>
        {isMobile ? (
          <>
            <PhotoListRunSelector fullWidth />
            {startNewRunButton}
            {/* Print anchors its panel left, Export right, so each dropdown
                opens within the viewport from its half of the row. */}
            <div style={{ display: "flex", gap: THEME.spacing.sm }}>
              <SheetSelectionDropdown
                triggerLabel="Print"
                leadingIcon={<Printer size={14} strokeWidth={2} />}
                busyLabel="Loading..."
                actionLabel="Print"
                onAction={handlePrint}
                disabled={currentRun === null}
                fullWidth
                panelAlign="left"
              />
              <ExportDropdown
                currentRunId={currentRun?.id ?? null}
                runStartedAt={currentRun?.started_at ?? null}
                dealerGroupId={dealerGroupId}
                fullWidth
              />
              {settingsButton}
            </div>
          </>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <PhotoListRunSelector />
            {startNewRunButton}
            <SheetSelectionDropdown
              triggerLabel="Print"
              leadingIcon={<Printer size={14} strokeWidth={2} />}
              busyLabel="Loading..."
              actionLabel="Print"
              onAction={handlePrint}
              disabled={currentRun === null}
            />
            <ExportDropdown
              currentRunId={currentRun?.id ?? null}
              runStartedAt={currentRun?.started_at ?? null}
              dealerGroupId={dealerGroupId}
            />
            {settingsButton}
          </div>
        )}
      </div>

      {generatedTotal != null && (
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: THEME.colors.semantic.success,
            marginBottom: THEME.spacing.sm,
          }}
        >
          Photo list generated ({generatedTotal.toLocaleString()} vehicles)
        </div>
      )}

      {/* Degradation banner — a successful run that proceeded without a
          vendor (no STOP flags, no transit colors, unfiltered Kia NEW...).
          Without this a silently thinner list reads as vendor truth; the
          warnings are harvested from the run's own output server-side. */}
      {currentRun?.degradation_notes && (
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: THEME.colors.semantic.warning,
            marginBottom: THEME.spacing.sm,
            whiteSpace: "pre-line",
          }}
        >
          This list was generated with warnings:
          {"\n"}
          {currentRun.degradation_notes}
        </div>
      )}

      {/* Divider */}
      <div
        style={{
          borderTop: `1px solid ${THEME.colors.silver}`,
          marginBottom: THEME.spacing.md,
        }}
      />

      {/* Tabs + data table */}
      <PhotoListTabs />

      {/* Add car rides beside the search on every width, so it costs no
          extra row on a phone. Only today's list takes hand-added cars
          (Ian, 2026-10-11); an older run is history. */}
      <div
        style={{
          display: "flex",
          alignItems: "stretch",
          gap: THEME.spacing.sm,
          marginBottom: THEME.spacing.md,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <SearchInput
            value={searchText}
            onChange={setSearchText}
            placeholder="Search VIN, stock, model…"
          />
        </div>
        {isLatestList && (
          <button type="button" onClick={() => setAddingCar(true)} style={addCarButtonStyle}>
            <Plus size={14} strokeWidth={2} />
            {onReshootsSheet ? "Add reshoot" : "Add car"}
          </button>
        )}
      </div>

      {vehiclesLoading && (
        <div
          style={{
            padding: THEME.spacing.xl,
            textAlign: "center",
            color: THEME.colors.neutral.light.textSecondary,
            fontSize: 14,
          }}
        >
          Loading {activeLabel}...
        </div>
      )}

      {vehiclesError && !vehiclesLoading && (
        <div
          style={{
            padding: THEME.spacing.lg,
            textAlign: "center",
            color: THEME.colors.semantic.danger,
            fontSize: 14,
          }}
        >
          Error loading vehicles: {vehiclesError}
        </div>
      )}

      {!vehiclesLoading && !vehiclesError && (
        <PhotoListTable
          vehicles={visibleVehicles}
          sheetSlug={activeSheet}
          sheetLabel={activeLabel}
          runDate={currentRun?.started_at ?? null}
          dealerGroupId={dealerGroupId}
          emptyMessage={
            searchText.trim() && vehicles.length > 0
              ? `No vehicles match "${searchText.trim()}".`
              : undefined
          }
          onRemoveAddedVehicle={isLatestList ? removeVehicleAddedByHand : undefined}
          onRemoveReshoot={isLatestList ? removeReshoot : undefined}
        />
      )}

      {addingCar && onReshootsSheet && (
        <RequestReshootModal
          dealerGroupId={dealerGroupId}
          onClose={() => setAddingCar(false)}
          onSubmit={requestReshoot}
        />
      )}
      {addingCar && !onReshootsSheet && (
        <AddCarToListModal
          // Not the Reshoots sheet, so a store sheet.
          defaultSheet={activeSheet as PhotoListStoreSheetSlug}
          onClose={() => setAddingCar(false)}
          onSubmit={addVehicleByHand}
        />
      )}

      {/* Print container — hidden on screen, visible only during print */}
      {printing && (
        <PrintableSheets
          vehiclesBySheet={printVehicles}
          selectedSheets={printSheets.selected}
          sheetOrder={printSheets.order}
          runDate={currentRun?.started_at ?? null}
        />
      )}
    </div>
  )
}

// Brand primary button, same tokens as startNewRunButton above (navy fill,
// md radius, 14px weight 500) with a leading + icon. No vertical padding:
// its row stretches it to the search box's height (Ian, 2026-10-11), which
// only works while the button's own height is the shorter of the two.
const addCarButtonStyle: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: THEME.spacing.xs,
  padding: `0 ${THEME.spacing.md}`,
  background: THEME.colors.navy,
  color: THEME.colors.white,
  fontWeight: 500,
  fontSize: 14,
  border: "none",
  borderRadius: THEME.borderRadius.md,
  cursor: "pointer",
  fontFamily: THEME.fonts.primary,
  whiteSpace: "nowrap",
}
